/**
 * Wind data types and utilities
 *
 * Fetches a GLOBAL wind grid from Open-Meteo API and provides bilinear
 * interpolation for the particle animation system. The grid is fetched
 * once and cached — no per-viewport fetching needed since wind patterns
 * are large-scale phenomena.
 */

export interface WindData {
  width: number;
  height: number;
  bounds: WindDataBounds;
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
  u: Float32Array;
  v: Float32Array;
}

export interface WindDataBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface WindForecastTime {
  time: Date;
  label: string;
}

// Global grid: 12 lon x 7 lat = 84 points (well within API limits)
const GLOBAL_BOUNDS: WindDataBounds = {
  west: -180,
  east: 180,
  south: -70,
  north: 70,
};
const GRID_WIDTH = 12;
const GRID_HEIGHT = 7;

// Cache
let windCache: {
  data: WindData;
  forecastHour: number;
  timestamp: number;
} | null = null;

const CACHE_DURATION = 15 * 60 * 1000; // 15 min
const BASE_REQUEST_INTERVAL = 30 * 1000;
let minRequestInterval = BASE_REQUEST_INTERVAL;
let lastRequestTime = 0;
let consecutiveErrors = 0;
let fetchInProgress: Promise<WindData> | null = null;

/**
 * Fetch global wind data from Open-Meteo.
 * Ignores bounds param — always fetches the full globe.
 * Deduplicates concurrent requests.
 */
export async function fetchWindData(
  _bounds: WindDataBounds,
  forecastHour: number = 0
): Promise<WindData> {
  // Use cache if valid
  if (windCache && windCache.forecastHour === forecastHour) {
    if (Date.now() - windCache.timestamp < CACHE_DURATION) {
      return windCache.data;
    }
  }

  // Deduplicate — if a fetch is already running, wait for it
  if (fetchInProgress) {
    return fetchInProgress;
  }

  const now = Date.now();
  if (now - lastRequestTime < minRequestInterval) {
    if (windCache) return windCache.data;
    return createCalmWindData();
  }
  lastRequestTime = now;

  fetchInProgress = doFetch(forecastHour);
  try {
    return await fetchInProgress;
  } finally {
    fetchInProgress = null;
  }
}

async function doFetch(forecastHour: number): Promise<WindData> {
  const lats: number[] = [];
  const lons: number[] = [];

  const lonStep = (GLOBAL_BOUNDS.east - GLOBAL_BOUNDS.west) / (GRID_WIDTH - 1);
  const latStep = (GLOBAL_BOUNDS.north - GLOBAL_BOUNDS.south) / (GRID_HEIGHT - 1);

  for (let y = 0; y < GRID_HEIGHT; y++) {
    for (let x = 0; x < GRID_WIDTH; x++) {
      lats.push(Number((GLOBAL_BOUNDS.north - y * latStep).toFixed(2)));
      lons.push(Number((GLOBAL_BOUNDS.west + x * lonStep).toFixed(2)));
    }
  }

  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', lats.join(','));
  url.searchParams.set('longitude', lons.join(','));
  url.searchParams.set('hourly', 'wind_speed_10m,wind_direction_10m');
  url.searchParams.set('wind_speed_unit', 'ms');
  url.searchParams.set('forecast_hours', String(Math.max(1, forecastHour + 1)));
  url.searchParams.set('timeformat', 'unixtime');

  try {
    console.log(`[Wind] Fetching global ${GRID_WIDTH}x${GRID_HEIGHT} grid (${GRID_WIDTH * GRID_HEIGHT} points)...`);
    const response = await fetch(url.toString());

    if (!response.ok) {
      if (response.status === 429) {
        consecutiveErrors++;
        minRequestInterval = Math.min(5 * 60 * 1000, BASE_REQUEST_INTERVAL * Math.pow(2, consecutiveErrors));
        console.warn(`[Wind] Rate limited (429). Backing off to ${Math.round(minRequestInterval / 1000)}s`);
        if (windCache) return windCache.data;
        return createCalmWindData();
      }
      throw new Error(`Wind API error: ${response.status}`);
    }

    consecutiveErrors = 0;
    minRequestInterval = BASE_REQUEST_INTERVAL;

    const data = await response.json();
    const windData = processWindResponse(data, GRID_WIDTH, GRID_HEIGHT, forecastHour, GLOBAL_BOUNDS);
    console.log(
      `[Wind] Global grid loaded: ${GRID_WIDTH}x${GRID_HEIGHT}, ` +
      `u:[${windData.uMin.toFixed(1)},${windData.uMax.toFixed(1)}] ` +
      `v:[${windData.vMin.toFixed(1)},${windData.vMax.toFixed(1)}]`
    );

    windCache = { data: windData, forecastHour, timestamp: Date.now() };
    return windData;
  } catch (error) {
    consecutiveErrors++;
    minRequestInterval = Math.min(5 * 60 * 1000, BASE_REQUEST_INTERVAL * Math.pow(2, consecutiveErrors));
    console.error('[Wind] Failed to fetch:', error);
    if (windCache) return windCache.data;
    return createCalmWindData();
  }
}

function processWindResponse(
  data: any,
  width: number,
  height: number,
  forecastHour: number,
  bounds: WindDataBounds
): WindData {
  const u = new Float32Array(width * height);
  const v = new Float32Array(width * height);

  const results = Array.isArray(data) ? data : [data];

  let uMin = Infinity, uMax = -Infinity;
  let vMin = Infinity, vMax = -Infinity;

  for (let i = 0; i < Math.min(results.length, width * height); i++) {
    const result = results[i];
    const hourly = result?.hourly;

    if (hourly && hourly.wind_speed_10m && hourly.wind_direction_10m) {
      const hourIndex = Math.min(forecastHour, hourly.wind_speed_10m.length - 1);
      const speed = hourly.wind_speed_10m[hourIndex];
      const direction = hourly.wind_direction_10m[hourIndex];

      if (speed != null && direction != null) {
        const dirRad = ((direction + 180) * Math.PI) / 180;
        const uVal = speed * Math.sin(dirRad);
        const vVal = speed * Math.cos(dirRad);

        u[i] = uVal;
        v[i] = vVal;

        uMin = Math.min(uMin, uVal);
        uMax = Math.max(uMax, uVal);
        vMin = Math.min(vMin, vVal);
        vMax = Math.max(vMax, vVal);
      }
    }
  }

  return {
    width,
    height,
    bounds,
    uMin: isFinite(uMin) ? uMin : -10,
    uMax: isFinite(uMax) ? uMax : 10,
    vMin: isFinite(vMin) ? vMin : -10,
    vMax: isFinite(vMax) ? vMax : 10,
    u,
    v,
  };
}

function createCalmWindData(): WindData {
  return {
    width: GRID_WIDTH, height: GRID_HEIGHT, bounds: GLOBAL_BOUNDS,
    uMin: 0, uMax: 0, vMin: 0, vMax: 0,
    u: new Float32Array(GRID_WIDTH * GRID_HEIGHT),
    v: new Float32Array(GRID_WIDTH * GRID_HEIGHT),
  };
}

export function generateWindTimeOptions(): WindForecastTime[] {
  const options: WindForecastTime[] = [];
  const now = new Date();
  now.setMinutes(0, 0, 0);

  for (let i = 0; i <= 48; i += 3) {
    const time = new Date(now.getTime() + i * 60 * 60 * 1000);
    const label = i === 0 ? 'Now' : `+${i}h`;
    options.push({ time, label });
  }

  return options;
}

/**
 * Get wind speed color — earth.nullschool.net inspired palette.
 * Returns [R, G, B, A].
 */
export function getWindSpeedColor(speed: number): [number, number, number, number] {
  if (speed < 1) return [98, 113, 183, 255];
  if (speed < 3) return [57, 97, 159, 255];
  if (speed < 5) return [74, 148, 169, 255];
  if (speed < 7) return [77, 167, 145, 255];
  if (speed < 9) return [85, 167, 100, 255];
  if (speed < 11) return [126, 175, 75, 255];
  if (speed < 13) return [184, 183, 63, 255];
  if (speed < 15) return [227, 176, 56, 255];
  if (speed < 20) return [241, 138, 54, 255];
  if (speed < 25) return [234, 93, 59, 255];
  if (speed < 30) return [213, 56, 71, 255];
  return [183, 55, 121, 255];
}

/**
 * Bilinear interpolation of wind vector at a geographic position.
 */
export function interpolateWind(
  lng: number,
  lat: number,
  data: WindData
): [number, number] {
  const { width, height, u, v, bounds } = data;

  // Wrap longitude into -180..180 for world-wrapping maps
  let normLng = ((lng + 180) % 360 + 360) % 360 - 180;

  const nx = (normLng - bounds.west) / (bounds.east - bounds.west);
  const ny = (bounds.north - lat) / (bounds.north - bounds.south);

  if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return [0, 0];

  const gx = nx * (width - 1);
  const gy = ny * (height - 1);

  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);

  const fx = gx - x0;
  const fy = gy - y0;

  const i00 = y0 * width + x0;
  const i10 = y0 * width + x1;
  const i01 = y1 * width + x0;
  const i11 = y1 * width + x1;

  const uVal =
    u[i00] * (1 - fx) * (1 - fy) +
    u[i10] * fx * (1 - fy) +
    u[i01] * (1 - fx) * fy +
    u[i11] * fx * fy;

  const vVal =
    v[i00] * (1 - fx) * (1 - fy) +
    v[i10] * fx * (1 - fy) +
    v[i01] * (1 - fx) * fy +
    v[i11] * fx * fy;

  return [uVal, vVal];
}
